/**
 * 맥스매치 운영 신규 등록 모달.
 * S3 등록 → 상세와 동일한 Document AI OCR → 후처리 → MR 일괄 등록까지 진행한다.
 * 이미지 확정·맥스매치 생성(`run`)은 상세에서 수동으로 실행한다.
 */
import { maxMatchOpsService } from '@/entities/max-match/api/max-match-ops.api';
import {
  maxMatchOpsKeys,
  useCreateMaxMatchOps,
  useCreateMaxMatchOpsPresignedUrls,
} from '@/entities/max-match/hooks/useMaxMatchOps';
import { ocrService } from '@/entities/ocr/api/ocr.api';
import {
  mapOcrResultToPostProcessRows,
  mapPostProcessItemsToMrPayloads,
  pickOcrYearMonth,
  resolveOpsCreateStepLabel,
} from '@/pages/max-match/maxMatchOpsCreatePipeline';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { uploadFileToPresignedUrl } from '@/shared/utils/fileUpload';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (summaryId: number) => void;
};

type LocalFile = {
  id: string;
  file: File;
  previewUrl: string;
};

export const MaxMatchOpsCreateDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onCreated,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const createPresigned = useCreateMaxMatchOpsPresignedUrls();
  const createOps = useCreateMaxMatchOps();

  const [pharmaCompanyName, setPharmaCompanyName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [files, setFiles] = useState<LocalFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) return;
    setPharmaCompanyName('');
    setClinicName('');
    setSubmitStep('');
    setFiles((prev) => {
      prev.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      return [];
    });
  }, [open]);

  const handleFilesSelected = (list: FileList | null) => {
    if (!list?.length) return;
    const next: LocalFile[] = Array.from(list).map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setFiles((prev) => [...prev, ...next]);
  };

  const removeFile = (id: string) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleSubmit = async () => {
    const pharma = pharmaCompanyName.trim();
    const clinic = clinicName.trim();
    if (!pharma) {
      toast.error('제공업체명을 입력해 주세요.');
      return;
    }
    if (!clinic) {
      toast.error('병의원명을 입력해 주세요.');
      return;
    }
    if (files.length === 0) {
      toast.error('처방 이미지를 1건 이상 선택해 주세요.');
      return;
    }

    setSubmitting(true);
    let createdSummaryId: number | null = null;
    try {
      const filenames = files.map((f) => f.file.name);
      setSubmitStep(resolveOpsCreateStepLabel({ kind: 'presign' }));
      const urls = await createPresigned.mutateAsync(filenames);
      if (urls.length !== files.length) {
        throw new Error('업로드 URL 개수가 파일 수와 일치하지 않습니다.');
      }

      for (let i = 0; i < files.length; i++) {
        setSubmitStep(
          resolveOpsCreateStepLabel({
            kind: 'upload',
            current: i + 1,
            total: files.length,
          }),
        );
        await uploadFileToPresignedUrl(files[i].file, urls[i].presignedUrl);
      }

      setSubmitStep(resolveOpsCreateStepLabel({ kind: 'create' }));
      const created = await createOps.mutateAsync({
        pharmaCompanyName: pharma,
        clinicName: clinic,
        images: urls.map((u, i) => ({
          shortKey: u.shortKey,
          uploadKey: u.uploadKey,
          originalFilename: files[i].file.name,
        })),
      });
      createdSummaryId = created.summaryId;

      const images = await maxMatchOpsService.getImages(created.summaryId);
      const confirmedImageIds: number[] = [];
      const failedImageIds: number[] = [];
      let processedCount = 0;

      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const total = images.length;
        if (!image.imageUrl) {
          failedImageIds.push(image.imageId);
          continue;
        }

        try {
          setSubmitStep(
            resolveOpsCreateStepLabel({
              kind: 'ocr',
              current: i + 1,
              total,
            }),
          );
          // 상세 OCR 버튼과 동일: Document AI process-url + 정규화
          const ocrResult = await ocrService.ocrPrescriptionFromUrl({
            imageUrl: image.imageUrl,
          });
          const postProcessRows = mapOcrResultToPostProcessRows(ocrResult);
          if (postProcessRows.length === 0) {
            failedImageIds.push(image.imageId);
            continue;
          }

          setSubmitStep(
            resolveOpsCreateStepLabel({
              kind: 'postProcess',
              current: i + 1,
              total,
            }),
          );
          const yearMonth = pickOcrYearMonth(ocrResult, created.refYm);
          const postProcess = await maxMatchOpsService.postProcessOcr({
            prescriptionYearMonth: yearMonth,
            source: 'ops-create',
            mode: 'APPLY',
            rows: postProcessRows,
          });
          const payloads = mapPostProcessItemsToMrPayloads(
            postProcess.items ?? [],
            postProcessRows,
          );
          if (payloads.length === 0) {
            failedImageIds.push(image.imageId);
            continue;
          }

          setSubmitStep(
            resolveOpsCreateStepLabel({
              kind: 'saveMrs',
              current: i + 1,
              total,
            }),
          );
          await maxMatchOpsService.addMrsBatch(image.imageId, payloads);
          confirmedImageIds.push(image.imageId);
          processedCount += 1;
        } catch {
          failedImageIds.push(image.imageId);
        }
      }

      // 미인식 이미지는 삭제. 확정·맥스매치 생성은 상세에서 수동
      if (confirmedImageIds.length > 0 && failedImageIds.length > 0) {
        await Promise.all(
          failedImageIds.map((imageId) =>
            maxMatchOpsService.deleteImage(imageId).catch(() => undefined),
          ),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: maxMatchOpsKeys.all,
      });

      if (confirmedImageIds.length === 0) {
        toast.warning(
          '운영 건은 생성됐지만 OCR 결과를 등록하지 못했습니다. 상세에서 이어서 진행해 주세요.',
        );
      } else if (failedImageIds.length > 0) {
        toast.success(
          `${processedCount}건 OCR·등록까지 완료했습니다. 미인식 ${failedImageIds.length}건은 제외했습니다. 확정·맥스매치 생성은 상세에서 진행해 주세요.`,
        );
      } else {
        toast.success(
          '맥스매치 운영 건을 등록했습니다. 확정·맥스매치 생성은 상세에서 진행해 주세요.',
        );
      }

      onOpenChange(false);
      onCreated(created.summaryId);
    } catch (e) {
      handleApiError(e, toast);
      if (createdSummaryId != null) {
        onOpenChange(false);
        onCreated(createdSummaryId);
      }
    } finally {
      setSubmitting(false);
      setSubmitStep('');
    }
  };

  const busy = submitting || createPresigned.isPending || createOps.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>맥스매치 운영 등록</DialogTitle>
          <DialogDescription>
            제공업체·병의원명과 처방 이미지를 등록합니다. 확인 시 S3 업로드,
            OCR, 후처리, 의약품 일괄 등록까지 진행합니다. 확정과 맥스매치
            생성은 상세에서 수동으로 실행합니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="ops-pharma">제공업체명</Label>
            <Input
              id="ops-pharma"
              value={pharmaCompanyName}
              onChange={(e) => setPharmaCompanyName(e.target.value)}
              placeholder="예: 루다제약"
              disabled={busy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ops-clinic">병의원명</Label>
            <Input
              id="ops-clinic"
              value={clinicName}
              onChange={(e) => setClinicName(e.target.value)}
              placeholder="예: 루다연합내과"
              disabled={busy}
            />
          </div>
          <div className="flex flex-col items-start gap-2">
            <Label htmlFor="ops-files">처방 내역</Label>
            <FileUploadButton
              inputId="ops-files"
              inputRef={fileInputRef}
              accept="image/*"
              multiple
              disabled={busy}
              resetOnSelect={false}
              onChange={(e) => {
                handleFilesSelected(e.target.files);
                e.target.value = '';
              }}
            >
              처방 내역 업로드
            </FileUploadButton>
            {files.length > 0 ? (
              <ul className="grid w-full grid-cols-3 gap-2 pt-1">
                {files.map((item) => (
                  <li
                    key={item.id}
                    className="relative overflow-hidden rounded-md border"
                  >
                    <img
                      src={item.previewUrl}
                      alt={item.file.name}
                      className="h-24 w-full object-cover"
                    />
                    <button
                      type="button"
                      className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
                      aria-label={`${item.file.name} 삭제`}
                      disabled={busy}
                      onClick={() => removeFile(item.id)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <p className="truncate px-1 py-0.5 text-[10px] text-muted-foreground">
                      {item.file.name}
                    </p>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        {busy && submitStep ? (
          <p className="text-sm text-muted-foreground">{submitStep}</p>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            취소
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={busy}>
            {busy ? '등록 중...' : '확인'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
